import axios, { type AxiosError } from 'axios';
import { logger } from '@/shared/observability/frontendLogger';
import { trackRumError } from '@/shared/observability/rum';
import { activeWorkspaceStore } from '@/shared/workspace/activeWorkspaceStore';

declare module 'axios' {
  // Per-request opt-out for the global 401 -> /login bounce. Use for
  // best-effort reads where a 401 is expected/recoverable (e.g. restoring a
  // possibly-stale chat session) and the caller handles the failure itself.
  export interface AxiosRequestConfig {
    skipAuthRedirect?: boolean;
  }
}

const getApiBaseUrl = (): string => {
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';
};

export const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

let getTokenFunction: (() => Promise<string | null>) | null = null;

export const setApiTokenGetter = (getter: () => Promise<string | null>) => {
  getTokenFunction = getter;
};

const GUEST_TOKEN_STORAGE_KEY = 'bquest_guest_session_token';
const GUEST_TOKEN_HEADER = 'x-guest-token';

/**
 * Reads the guest session token (minted after the invisible reCAPTCHA) so
 * anonymous callers can reach the public AI endpoints. Logged-in users send a
 * Bearer token instead and never need this.
 */
const getGuestToken = (): string | null => {
  try {
    return sessionStorage.getItem(GUEST_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

apiClient.interceptors.request.use(
  async (config) => {
    let hasAuthToken = false;
    if (getTokenFunction) {
      try {
        const token = await getTokenFunction();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          hasAuthToken = true;
        }
      } catch (error) {
        console.error('Failed to get auth token:', error);
      }
    }

    // Attach the guest session token only when there is no logged-in user.
    if (!hasAuthToken) {
      const guestToken = getGuestToken();
      if (guestToken) {
        config.headers[GUEST_TOKEN_HEADER] = guestToken;
      }
    }

    // Feature 075 (workspace sub-delegation): when a SUB_DELEGATE has an
    // active workspace selected, every request carries the owner id so the
    // backend WorkspaceGuard can resolve the delegated context. The store
    // is never set for non-delegate users, so this is a no-op for them.
    const workspaceOwnerId = activeWorkspaceStore.get();
    if (workspaceOwnerId) {
      config.headers['x-workspace-owner-id'] = workspaceOwnerId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

type ApiResponseData = {
  message?: string;
  errors?: unknown;
  detail?: string;
  error?: {
    code?: string;
    message?: string;
    retryable?: boolean;
    retry_after?: number | null;
    request_id?: string | null;
  };
  // File-level rejection shape for e.g. `POST /admin/referrals/manual/bulk`
  // (spec §7.1) — a 4xx with `{ fileErrors }` and no `errors` envelope.
  fileErrors?: unknown;
};

export const handleResponseError = (error: unknown) => {
  const err = error as AxiosError<ApiResponseData>;
  const isNetworkError =
    axios.isAxiosError(error) && !err.response && err.code !== 'ERR_CANCELED';
  const url = err.config?.url || '';
  const method = (err.config?.method || 'UNKNOWN').toUpperCase();

  if (isNetworkError) {
    const message = err.message || 'Network request failed';
    logger.error(`Network Error: ${message}`, {
      errorType: 'network',
      originalError: err.code,
    });

    const rumError = new Error(`Network: ${message}`);
    rumError.name = 'NetworkError';
    trackRumError(rumError, { errorType: 'network', errorCode: err.code });

    return Promise.reject({
      message,
      statusCode: 0,
      errors: undefined,
      code: undefined,
      retryable: false,
      retryAfter: null,
      requestId: null,
    });
  }

  const statusCode = err.response?.status || 0;
  const errorEnvelope = err.response?.data?.error;
  const message =
    errorEnvelope?.message ||
    err.response?.data?.detail ||
    err.response?.data?.message ||
    err.message ||
    'An unexpected error occurred';

  logger.error(`API Error: ${method} ${url} → ${statusCode}`, {
    url,
    method,
    statusCode,
    responseMessage: message,
    errors: err.response?.data?.errors,
  });

  const rumError = new Error(`API ${statusCode}: ${message}`);
  rumError.name = 'ApiError';
  trackRumError(rumError, { url, method, statusCode });

  const apiError = {
    message,
    statusCode: statusCode || 500,
    errors: err.response?.data?.errors,
    // `code` may arrive in the error envelope or at the top level (the global
    // exception filter passes HttpException `code` fields through top-level).
    code: errorEnvelope?.code ?? (err.response?.data as { code?: string } | undefined)?.code,
    retryable: errorEnvelope?.retryable ?? false,
    retryAfter: errorEnvelope?.retry_after ?? null,
    requestId: errorEnvelope?.request_id ?? null,
    fileErrors: err.response?.data?.fileErrors,
  };

  // Feature 100: an expired trial FP is blocked platform-wide — route them to
  // the dedicated "trial ended" screen instead of surfacing raw 403s.
  if (apiError.statusCode === 403 && apiError.code === 'TRIAL_EXPIRED') {
    if (!window.location.pathname.startsWith('/trial-ended')) {
      window.location.href = '/trial-ended';
    }
    return Promise.reject(apiError);
  }

  const isAuthEndpoint = url.includes('/auth/');
  // Guest (anonymous) calls carry x-guest-token and must NOT bounce to /login on
  // 401 — the caller handles re-minting the guest session instead.
  const isGuestRequest = Boolean(
    (err.config?.headers as Record<string, unknown> | undefined)?.['x-guest-token'],
  );
  const skipAuthRedirect = Boolean(err.config?.skipAuthRedirect);
  if (apiError.statusCode === 401 && !isAuthEndpoint && !isGuestRequest && !skipAuthRedirect) {
    console.warn('Unauthorized request detected, redirecting to login');
    window.location.href = '/login';
  }

  return Promise.reject(apiError);
};

apiClient.interceptors.response.use((response) => response, handleResponseError);

